import { KPI_TEXT_SHADOW } from '@/components/ui/KpiCard';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ImageOff, X, Truck, Check, Lightbulb } from '@/components/icons';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/form/Input';
import { useToast } from '@/components/ui/useToast';
import { usePrompt } from '@/components/ui/usePrompt';
import {
  approveEnquiryFull,
  approveEnquiryPartial,
  getEnquiry,
  getEnquiryMargin,
  raiseShortageMrn,
  rejectEnquiry,
  type Enquiry,
} from '@/api/enquiry';
import { formatDate, formatINR, formatINRSymbol } from '@/lib/format';
import { apiError } from '@/lib/apiError';
import { orderRef } from '@/lib/orderRef';

type Tab = 'details' | 'items' | 'stock' | 'margin' | 'credit' | 'actions';

interface Props {
  open: boolean;
  enqId: number | null;
  onClose: () => void;
}

const STOCK_TONE = { GREEN: 'success', AMBER: 'warning', RED: 'danger' } as const;

/**
 * Reviewed but approved for less than ordered on some line, and no Dispatch Order yet
 * (it would be stage "do-created" then) — the rest can still be approved from Review.
 */
function isPartlyApproved(enq: Pick<Enquiry, 'stage' | 'balancePending' | 'items'>): boolean {
  return enq.stage === 'reviewed' && !enq.balancePending &&
    enq.items.some((it) => it.approvedQty != null && Number(it.approvedQty) < Number(it.qty) - Number(it.dispatchedQty) - 1e-6);
}
const CREDIT_TONE = { GREEN: 'success', AMBER: 'warning', RED: 'danger' } as const;

export function EnqReviewModal({ open, enqId, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const ask = usePrompt();
  const [tab, setTab] = useState<Tab>('details');
  const [approvedQty, setApprovedQty] = useState<Record<number, string>>({});

  const enqQuery = useQuery({
    queryKey: ['enquiries', enqId],
    queryFn: () => getEnquiry(enqId as number),
    enabled: open && !!enqId,
  });
  const enq = enqQuery.data ?? null;

  const marginQuery = useQuery({
    queryKey: ['enquiry-margin', enqId],
    queryFn: () => getEnquiryMargin(enqId as number),
    enabled: open && !!enqId && tab === 'margin',
  });

  useEffect(() => {
    if (!open) {
      setTab('details');
      setApprovedQty({});
      return;
    }
    if (enq) {
      const init: Record<number, string> = {};
      enq.items.forEach((it) => {
        // Pre-fill with the in-stock qty that can actually be approved now:
        // remaining balance (ordered − dispatched) minus the live shortfall.
        // Short lines default to 0 (auto-excluded); fully-stocked lines default
        // to their full balance. Keeps Approve Partial/Balance ready without
        // having to zero every short line by hand.
        const balance = Number(it.qty) - Number(it.dispatchedQty);
        if (isPartlyApproved(enq)) {
          // Approving the rest: the box is the qty to approve NOW — what's left, as far as free stock goes.
          const prior = Number(it.approvedQty ?? 0);
          const free = Math.max(0, (it.availableStock ?? 0) - prior);
          init[it.id] = String(Math.max(0, Math.min(balance - prior, free)));
        } else {
          init[it.id] = String(Math.max(0, balance - Number(it.shortfall ?? 0)));
        }
      });
      setApprovedQty(init);
    }
  }, [open, enq]);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['enquiries'] });
    qc.invalidateQueries({ queryKey: ['mrn'] });
    qc.invalidateQueries({ queryKey: ['mrn-pending'] });
  }

  const approveFullMutation = useMutation({
    mutationFn: () => approveEnquiryFull(enqId as number),
    onSuccess: () => { invalidate(); toast.push('Enquiry approved (full qty)', 'success'); onClose(); },
    onError: (e: unknown) => toast.push(apiError(e, 'Approve failed'), 'error'),
  });

  const approvePartialMutation = useMutation({
    mutationFn: () => {
      // Partly approved: send each line's new TOTAL (earlier approval + the extra entered now).
      const items = enq && isPartlyApproved(enq)
        ? enq.items.map((it) => ({ enqItemId: it.id, approvedQty: Number(it.approvedQty ?? 0) + Number(approvedQty[it.id] ?? 0) }))
            .filter((x) => Number.isFinite(x.approvedQty))
        : Object.entries(approvedQty)
            .map(([k, v]) => ({ enqItemId: Number(k), approvedQty: Number(v) }))
            .filter((x) => Number.isFinite(x.approvedQty));
      return approveEnquiryPartial(enqId as number, items);
    },
    onSuccess: () => { invalidate(); toast.push('Enquiry approved (partial)', 'success'); onClose(); },
    onError: (e: unknown) => toast.push(apiError(e, 'Approve failed'), 'error'),
  });

  const rejectMutation = useMutation({
    mutationFn: (reason: string) => rejectEnquiry(enqId as number, reason),
    onSuccess: () => { invalidate(); toast.push('Enquiry rejected', 'info'); onClose(); },
    onError: (e: unknown) => toast.push(apiError(e, 'Reject failed'), 'error'),
  });

  const raiseMrnMutation = useMutation({
    mutationFn: () => raiseShortageMrn(enqId as number),
    onSuccess: (res) => {
      invalidate();
      toast.pushStep(`Raised ${res.count} shortage MRN${res.count === 1 ? '' : 's'}`, {
        body: 'The purchase pipeline picks these up next.',
        href: '/purchase/mrn',
        nextLabel: 'Open MRN list',
        tag: `shortage-mrn-${enqId}`,
      });
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Raise MRN failed'), 'error'),
  });

  async function onReject() {
    const reason = await ask({ title: `Reject ${enq?.enqNo ?? 'order'}?`, label: 'Reason', multiline: true, tone: 'danger', confirmLabel: 'Reject' });
    if (!reason?.trim()) return;
    rejectMutation.mutate(reason);
  }

  const shortLines = enq?.items.filter((it) => Number(it.shortfall) > 0) ?? [];
  const hasAnyShortage = shortLines.length > 0;
  const balanceOf = (it: { qty: number | string; dispatchedQty: number | string }) =>
    Number(it.qty) - Number(it.dispatchedQty);
  // Partly-approved order: each box holds the qty to approve NOW, added to what was
  // already approved (so a line shows 4, not 9 = 5 approved + 4).
  const partlyApproved = enq ? isPartlyApproved(enq) : false;
  const canAct = !!enq && (enq.stage === 'new' || enq.balancePending || partlyApproved);
  const priorOf = (it: { approvedQty: string | null }) => (partlyApproved ? Number(it.approvedQty ?? 0) : 0);
  /** Free stock the rest can draw on: available minus what this order already holds approved. */
  const freeOf = (it: { approvedQty: string | null; availableStock?: number }) => Math.max(0, (it.availableStock ?? 0) - priorOf(it));
  /** Qty entered in the box (for a partly-approved order: the extra on top of the earlier approval). */
  const enteredOf = (it: { id: number; qty: number | string; dispatchedQty: number | string; approvedQty: string | null }) =>
    Number(approvedQty[it.id] ?? (partlyApproved ? 0 : balanceOf(it)));
  /** Total approved qty the line will have after this approval. */
  const proposedOf = (it: { id: number; qty: number | string; dispatchedQty: number | string; approvedQty: string | null }) =>
    priorOf(it) + enteredOf(it);
  // Can't approve more than the remaining balance. Live stock sufficiency is
  // enforced by the backend on submit (it reads current − reserved).
  const partialViolatesStock = enq?.items.some((it) => {
    const proposed = proposedOf(it);
    return Number.isFinite(proposed) && proposed > balanceOf(it) + 1e-6;
  }) ?? false;
  // Partial approval is fine as long as each line is approved for no MORE than
  // its in-stock qty — excluding a short line (0, or any qty ≤ available) is
  // allowed. Only approving MORE than is in stock blocks it. For a partly-approved
  // order only the extra entered now has to fit the free stock.
  const partialExceedsStock = enq?.items.some((it) => {
    if (partlyApproved) {
      const extra = enteredOf(it);
      return Number.isFinite(extra) && extra > freeOf(it) + 1e-6;
    }
    const proposed = proposedOf(it);
    const available = Math.max(0, balanceOf(it) - Number(it.shortfall ?? 0));
    return Number.isFinite(proposed) && proposed > available + 1e-6;
  }) ?? false;
  // At least one line must be approved for a positive qty (partly approved: something extra).
  const anyApproved = enq?.items.some((it) => (partlyApproved ? enteredOf(it) : proposedOf(it)) > 0) ?? false;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={enq ? `Review — ${orderRef(enq)} · ${enq.party.name}` : 'Review Enquiry'}
      size="xl"
      footer={
        tab === 'actions' && enq && canAct ? (
          <>
            <Button variant="glass" onClick={onClose}>Close</Button>
            <Button
              variant="danger"
              onClick={onReject}
              disabled={rejectMutation.isPending}
            >
              <X size={14} className="mr-1 inline-block align-[-2px]" /> Reject
            </Button>
            <Button
              variant="warning"
              onClick={() => raiseMrnMutation.mutate()}
              disabled={raiseMrnMutation.isPending || !(hasAnyShortage || enq.hasPendingQty)}
              title={hasAnyShortage || enq.hasPendingQty ? 'Auto-create one MRN covering every short line' : 'No shortfall on this order — nothing to procure'}
            >
              <Truck size={14} className="mr-1 inline-block align-[-2px]" /> Raise MRN for Shortfall
            </Button>
            <Button
              variant="info"
              onClick={() => approvePartialMutation.mutate()}
              disabled={approvePartialMutation.isPending || partialViolatesStock || partialExceedsStock || !anyApproved}
              title={
                partialExceedsStock
                  ? 'One or more lines are approved for more than the in-stock qty. Reduce them (set short lines to 0 to exclude).'
                  : partialViolatesStock
                    ? 'One or more Approve Qty values exceed the remaining balance. Reduce them first.'
                    : !anyApproved
                      ? 'Enter an approve qty on at least one line.'
                      : enq.balancePending
                        ? 'Approve the pending balance at the qty entered above → raises the next DO'
                        : partlyApproved
                          ? 'Approve the rest at the qty entered above (adds to what is already approved)'
                          : 'Approve each line at the qty entered above'
              }
            >
              {enq.balancePending ? 'Approve Balance' : partlyApproved ? 'Approve Remaining' : 'Approve Partial'}
            </Button>
            <Button
              variant="success"
              onClick={() => approveFullMutation.mutate()}
              disabled={approveFullMutation.isPending || hasAnyShortage || enq.balancePending || partlyApproved || !anyApproved}
              title={
                partlyApproved
                  ? 'Already partly approved — use Approve Remaining for the rest.'
                  : enq.balancePending
                  ? 'This order is partially dispatched — use Approve Balance for the remaining qty.'
                  : hasAnyShortage
                  ? `Blocked — ${shortLines.length} line(s) short of stock. Raise MRN for Shortfall; approve once the stock is refilled (GREEN).`
                  : !anyApproved
                  ? 'Nothing to approve — enter an approved qty greater than 0 for at least one line.'
                  : 'Approve every line at requested qty'
              }
            >
              <Check size={14} className="mr-1 inline-block align-[-2px]" /> Approve Full
            </Button>
          </>
        ) : (
          <Button variant="glass" onClick={onClose}>Close</Button>
        )
      }
    >
      {/* Tabs */}
      <div className="mb-4 flex flex-wrap gap-1 rounded-md border border-[var(--border-color)] bg-[var(--card-bg)] p-1">
        {(['details', 'items', 'stock', 'margin', 'credit', 'actions'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={clsx(
              'rounded px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition',
              tab === t
                ? 'bg-gradient-to-br from-accent to-[#e07b00] text-white'
                : 'text-[var(--text-secondary)] hover:text-accent',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {enqQuery.isFetching && !enq ? (
        <div className="p-10 text-center text-sm text-[var(--text-muted)]">Loading…</div>
      ) : !enq ? null : (
        <>
          {tab === 'details' && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="ENQ Date">{formatDate(enq.enqDate)}</Field>
              <Field label="Customer">{enq.party.name} <span className="text-[var(--text-muted)]">({enq.party.customerType ?? '—'})</span></Field>
              <Field label="Customer PO Ref">{enq.customerPoRef ?? '—'}</Field>
              <Field label="Expected Delivery">{formatDate(enq.expectedDelivery ?? '') || '—'}</Field>
              <Field label="Priority">{enq.priority}</Field>
              <Field label="Sales Rep">{enq.salesRep}</Field>
              <Field label="Source">{enq.source ?? '—'}</Field>
              <Field label="Stage">{enq.stage}</Field>
              <Field label="Delivery Address" className="col-span-2">
                <span className="font-normal text-[var(--text-secondary)]">{enq.deliveryAddress ?? '—'}</span>
              </Field>
              <Field label="Remarks" className="col-span-2">
                <span className="font-normal italic text-[var(--text-muted)]">{enq.remarks ? `"${enq.remarks}"` : '—'}</span>
              </Field>
            </div>
          )}

          {tab === 'items' && (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-accent/[0.05]">
                  <th className="p-2 text-center text-[10px] uppercase text-[var(--text-muted)]">Item</th>
                  <th className="p-2 text-left text-[10px] uppercase text-[var(--text-muted)]">SKU Barcode</th>
                  <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">Qty</th>
                  <th className="p-2 text-left text-[10px] uppercase text-[var(--text-muted)]">Unit</th>
                  <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">Rate</th>
                  <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">Est Value</th>
                  <th className="p-2 text-center text-[10px] uppercase text-[var(--text-muted)]">Stock</th>
                </tr>
              </thead>
              <tbody>
                {enq.items.map((it) => (
                  <tr key={it.id} className="border-b border-white/[0.04]">
                    <td className="p-2 font-bold">
                      <div className="flex items-center gap-2">
                        <ItemThumb src={it.item?.imageUrl} />
                        <span className="min-w-0">{it.itemName}</span>
                      </div>
                    </td>
                    <td className="p-2 font-mono text-[11px] font-bold text-[var(--text-primary)]">{it.item?.barcode ?? '—'}</td>
                    <td className="p-2 text-right">{it.qty}</td>
                    <td className="p-2">{it.unit}</td>
                    <td className="p-2 text-right">{formatINRSymbol(it.rate)}</td>
                    <td className="p-2 text-right font-bold text-accent">{formatINRSymbol(it.estValue)}</td>
                    <td className="p-2 text-center">
                      {it.stockStatus ? <Badge tone={STOCK_TONE[it.stockStatus]}>{it.stockStatus}</Badge> : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {tab === 'stock' && (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-accent/[0.05]">
                  <th className="p-2 text-center text-[10px] uppercase text-[var(--text-muted)]">Item</th>
                  <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">Req Qty</th>
                  <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">Available</th>
                  <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">Shortfall</th>
                  <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">Pending</th>
                  <th className="p-2 text-left text-[10px] uppercase text-[var(--text-muted)]">Auto MRN</th>
                  <th className="p-2 text-center text-[10px] uppercase text-[var(--text-muted)]">Status</th>
                </tr>
              </thead>
              <tbody>
                {enq.items.map((it) => (
                  <tr key={it.id} className="border-b border-white/[0.04]">
                    <td className="p-2 font-bold">
                      <div className="flex items-center gap-2">
                        <ItemThumb src={it.item?.imageUrl} />
                        <span className="min-w-0">
                          {it.itemName}
                          {it.item?.barcode && <span className="block text-[10px] font-normal text-[var(--text-muted)]">SKU: {it.item.barcode}</span>}
                        </span>
                      </div>
                    </td>
                    <td className="p-2 text-right">{it.qty} {it.unit}</td>
                    <td className={`p-2 text-right font-bold ${(it.availableStock ?? 0) <= 0 ? 'text-danger' : (it.availableStock ?? 0) < Number(it.qty) - Number(it.dispatchedQty) ? 'text-warning' : 'text-success'}`}>{it.availableStock ?? 0} {it.unit}</td>
                    <td className={`p-2 text-right ${Number(it.shortfall) > 0 ? 'font-extrabold text-danger' : 'text-[var(--text-muted)]'}`}>{it.shortfall}</td>
                    <td className="p-2 text-right">{it.pendingQty}</td>
                    <td className="p-2 font-mono text-[11px] text-[#a78bfa]">{it.autoMrnRef ?? '—'}</td>
                    <td className="p-2 text-center">
                      {it.stockStatus ? <Badge tone={STOCK_TONE[it.stockStatus]}>{it.stockStatus}</Badge> : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {tab === 'margin' && (
            <>
              {marginQuery.isFetching && !marginQuery.data && (
                <div className="p-10 text-center text-sm text-[var(--text-muted)]">Computing margin…</div>
              )}
              {marginQuery.data && (
                <>
                  <div className="mb-3 grid grid-cols-4 gap-3">
                    <Kpi label="Revenue" value={formatINRSymbol(marginQuery.data.revenue)} tone="info" />
                    <Kpi label="COGS" value={formatINRSymbol(marginQuery.data.cogs)} tone="warning" />
                    <Kpi label="Gross Profit" value={formatINRSymbol(marginQuery.data.gp)} tone={marginQuery.data.gp > 0 ? 'success' : 'danger'} />
                    <Kpi label="GP %" value={`${marginQuery.data.gpPct}%`} tone={marginQuery.data.gpPct >= 20 ? 'success' : 'warning'} />
                  </div>
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-accent/[0.05]">
                        <th className="p-2 text-center text-[10px] uppercase text-[var(--text-muted)]">Item</th>
                        <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">Qty</th>
                        <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">Revenue</th>
                        <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">COGS</th>
                        <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">GP</th>
                        <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">GP%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marginQuery.data.lines.map((l) => (
                        <tr key={l.enqItemId} className="border-b border-white/[0.04]">
                          <td className="p-2">{l.itemName}</td>
                          <td className="p-2 text-right">{l.qty}</td>
                          <td className="p-2 text-right">₹{formatINR(l.revenue)}</td>
                          <td className="p-2 text-right text-warning">₹{formatINR(l.cogs)}</td>
                          <td className={`p-2 text-right font-bold ${l.gp > 0 ? 'text-success' : 'text-danger'}`}>₹{formatINR(l.gp)}</td>
                          <td className={`p-2 text-right ${l.gpPct >= 20 ? 'text-success' : 'text-warning'}`}>{l.gpPct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}

          {tab === 'credit' && (
            <div className="space-y-3 text-sm">
              {enq.creditStatus && (
                <div
                  className={clsx(
                    'rounded-card border p-4',
                    enq.creditStatus === 'GREEN' && 'border-success/40 bg-success/[0.08]',
                    enq.creditStatus === 'AMBER' && 'border-warning/40 bg-warning/[0.08]',
                    enq.creditStatus === 'RED' && 'border-danger/40 bg-danger/[0.08]',
                  )}
                >
                  <div className="mb-1 flex items-center gap-2">
                    <Badge tone={CREDIT_TONE[enq.creditStatus]}>{enq.creditStatus}</Badge>
                    <span className="font-extrabold">Credit Status</span>
                  </div>
                  <div className="text-[var(--text-secondary)]">{enq.creditNote ?? '—'}</div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Order Value">{formatINRSymbol(enq.estOrderValue)}</Field>
                <Field label="Stage">{enq.stage}</Field>
              </div>
            </div>
          )}

          {tab === 'actions' && (
            <div className="space-y-4">
              <p className="text-sm text-[var(--text-secondary)]">
                {enq.balancePending ? (
                  <>This order is <strong>partially dispatched</strong>. Approve the pending <strong>balance</strong> below to raise the next Dispatch Order for the remaining items.</>
                ) : partlyApproved ? (
                  <>This order is <strong>partly approved</strong>. Enter the qty to approve <strong>now</strong> on each line (it&apos;s added to what&apos;s already approved), then use <strong>Approve Remaining</strong> — or <strong>Raise MRN for Shortfall</strong> for what isn&apos;t in stock.</>
                ) : (
                  <>Adjust per-line approved qty below, then use <strong>Approve Partial</strong>, or <strong>Approve Full</strong> to accept all lines at requested qty.</>
                )}
              </p>
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-accent/[0.05]">
                    <th className="p-2 text-center text-[10px] uppercase text-[var(--text-muted)]">Item</th>
                    <th className="p-2 text-left text-[10px] uppercase text-[var(--text-muted)]">SKU Barcode</th>
                    <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">Ordered</th>
                    <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">Dispatched</th>
                    <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">Available</th>
                    <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">{partlyApproved ? 'Approve Now' : 'Approve Qty'}</th>
                    <th className="p-2 text-center text-[10px] uppercase text-[var(--text-muted)]">Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {enq.items.map((it) => {
                    const ordered = Number(it.qty);
                    const dispatched = Number(it.dispatchedQty);
                    const balance = ordered - dispatched;
                    const available = it.availableStock ?? 0;
                    const prior = priorOf(it);
                    const left = Math.max(0, balance - prior); // still to approve (partly approved)
                    const free = freeOf(it);
                    const entered = enteredOf(it);
                    const proposed = proposedOf(it);
                    const overCap = Number.isFinite(proposed) && proposed > balance + 1e-6;
                    // Partly approved: only the extra entered now has to fit the free stock.
                    const overAvail = partlyApproved
                      ? Number.isFinite(entered) && entered > free + 1e-6
                      : Number.isFinite(proposed) && proposed > available + 1e-6;
                    // Nothing left to approve on a fully-approved line.
                    // Nothing left on the line (fully dispatched, or fully approved) → its box is locked.
                    const canEdit = canAct && balance > 1e-6 && !(partlyApproved && left <= 1e-6);
                    return (
                      <tr key={it.id} className="border-b border-white/[0.04]">
                        <td className="p-2 font-bold">
                          <div className="flex items-center gap-2">
                            <ItemThumb src={it.item?.imageUrl} />
                            <span className="min-w-0">{it.itemName}</span>
                          </div>
                        </td>
                        <td className="p-2 font-mono text-[11px] font-bold text-[var(--text-primary)]">{it.item?.barcode ?? '—'}</td>
                        <td className="p-2 text-right">{ordered} {it.unit}</td>
                        <td className="p-2 text-right text-[var(--text-muted)]">{dispatched} {it.unit}</td>
                        <td className={clsx('p-2 text-right font-bold', available <= 0 ? 'text-danger' : available < balance ? 'text-warning' : 'text-success')}>
                          {available} {it.unit}
                        </td>
                        <td className="p-2 text-right">
                          <Input
                            type="number"
                            step="1"
                            min={0}
                            max={partlyApproved ? Math.min(left, free) : Math.min(balance, Math.max(0, available))}
                            value={approvedQty[it.id] ?? ''}
                            onChange={(e) => setApprovedQty((s) => ({ ...s, [it.id]: e.target.value }))}
                            className={clsx('text-right', (overCap || overAvail) && 'border-danger')}
                            disabled={!canEdit}
                          />
                          {overCap ? (
                            <div className="mt-1 text-[10px] font-semibold text-danger">{partlyApproved ? `Only ${left} left to approve` : `Exceeds balance ${balance}`}</div>
                          ) : overAvail ? (
                            <div className="mt-1 text-[10px] font-semibold text-danger">{partlyApproved ? `Only ${free} free in stock — approve ≤ this` : `Only ${available} available — approve ≤ this`}</div>
                          ) : null}
                          {partlyApproved ? (
                            left <= 1e-6 ? (
                              Number(it.shortfall) > 0 ? (
                                // Approved earlier, but those pieces are no longer free in the warehouse.
                                <div className="mt-1 text-[10px] font-semibold text-danger">
                                  Approved {prior}, but only {Math.max(0, available)} in stock — {String(it.shortfall)} short · Raise MRN
                                </div>
                              ) : (
                                <div className="mt-1 text-[10px] font-semibold text-success">Fully approved ({prior}) · in stock</div>
                              )
                            ) : (
                              <div className="mt-1 text-[10px] font-semibold text-amber-500">
                                Already approved {prior} · {left} left
                                {Number(it.shortfall) > 0 ? ` · ${String(it.shortfall)} short in stock` : ''}
                              </div>
                            )
                          ) : dispatched > 0 ? (
                            balance > 1e-6
                              ? <div className="mt-1 text-[10px] font-semibold text-amber-500">Balance {balance} pending</div>
                              : <div className="mt-1 text-[10px] font-semibold text-success">Fully dispatched</div>
                          ) : Number(it.shortfall) > 0 ? (
                            <div className="mt-1 text-[10px] font-semibold text-warning">
                              {String(it.shortfall)} short — stock re-checked on submit
                            </div>
                          ) : null}
                        </td>
                        <td className="p-2 text-center">
                          {it.stockStatus ? <Badge tone={STOCK_TONE[it.stockStatus]}>{it.stockStatus}</Badge> : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {enq.hasPendingQty && (
                <div className="rounded-md border border-warning/40 bg-warning/[0.08] p-3 text-xs text-warning">
                  <Lightbulb size={14} className="mr-1 inline-block align-[-2px]" /> Some lines have shortage. Use <strong>Raise MRN for Shortfall</strong> to auto-create
                  procurement MRNs (one per shortage line, source = Sales-Short).
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={clsx(className)}>
      <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
      <div className="mt-0.5 text-sm font-bold text-[var(--text-primary)]">{children}</div>
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: React.ReactNode; tone: 'info' | 'warning' | 'success' | 'danger' }) {
  const cls =
    tone === 'success' ? 'bg-success text-white'
      : tone === 'warning' ? 'bg-warning text-white'
      : tone === 'danger' ? 'bg-danger text-white'
      : 'bg-info text-white';
  return (
    <div className={clsx('rounded-card p-3 shadow-sm', cls)} style={{ textShadow: KPI_TEXT_SHADOW }}>
      <div className="text-[10px] font-bold uppercase tracking-wide opacity-90">{label}</div>
      <div className="text-lg font-black">{value}</div>
    </div>
  );
}

/** Small item thumbnail for the review tables — the item-master image, or a
 *  neutral placeholder when the item has no picture. */
function ItemThumb({ src }: { src?: string | null }) {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        className="h-9 w-9 shrink-0 rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] object-cover"
        onError={(e) => {
          const el = e.currentTarget;
          el.onerror = null;
          el.replaceWith(Object.assign(document.createElement('div'), {
            className: 'flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-muted)]',
          }));
        }}
      />
    );
  }
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-muted)]">
      <ImageOff size={14} />
    </div>
  );
}
